import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatters';
import { useTrialDays } from '@/hooks/useSubscription';
import { PLANO_CARDS } from './constants';
import type { SubscriptionModalFormState } from './useSubscriptionModalForm';

interface SummaryBannerProps {
  form: SubscriptionModalFormState;
  atualLabel: string;
}

// Banner de resumo: "<atual> → <Plano> <Ciclo>, R$ X / mês (ou / ano). Trial encerra ao aplicar."
// (modo Padrão) ou "<atual> → Enterprise <vigência>, R$ <total> (<N>× R$ <parcela>). ..."
// (modo Enterprise). Reflete a seleção corrente do form.
export function SummaryBanner({ form, atualLabel }: SummaryBannerProps) {
  const { data: trialDays } = useTrialDays();
  const cardMeta = PLANO_CARDS.find((c) => c.card === form.planoCard);
  const isEnterprise = form.planoCard === 'enterprise';

  let destino: string;
  if (form.planoCard === 'trial') {
    destino = trialDays != null ? `Teste ${trialDays} dias` : (cardMeta?.label ?? 'Teste grátis');
  } else if (isEnterprise) {
    const vigenciaLabel = `${form.vigenciaAnos} ano${form.vigenciaAnos > 1 ? 's' : ''}`;
    const totalLabel =
      form.parcela != null ? formatCurrency(Number(form.valorTotal.replace(',', '.'))) : '—';
    const parcelaLabel =
      form.formaPagamento === 'mensal' && form.parcela != null
        ? ` (${form.nMeses}× ${formatCurrency(form.parcela)})`
        : '';
    destino = form.enterpriseCortesia
      ? `Enterprise ${vigenciaLabel} · cortesia (grátis)`
      : `Enterprise ${vigenciaLabel}, ${totalLabel}${parcelaLabel}`;
  } else {
    const cicloLabel = form.ciclo === 'anual' ? 'Anual' : 'Mensal';
    if (form.cortesia) {
      destino = `${cardMeta?.label ?? ''} ${cicloLabel} · cortesia (grátis)`;
    } else {
      const valorLabel =
        form.valorPagoNumero != null
          ? `${formatCurrency(form.valorPagoNumero)} / ${form.ciclo === 'anual' ? 'ano' : 'mês'}`
          : '—';
      destino = `${cardMeta?.label ?? ''} ${cicloLabel}, ${valorLabel}`;
    }
  }

  return (
    <div
      role="status"
      className={cn(
        'rounded-lg border px-4 py-3 text-sm',
        isEnterprise
          ? 'border-tertiary/30 bg-tertiary/5 text-text-title'
          : 'border-primary/30 bg-primary/5 text-text-title'
      )}
    >
      <span className="font-medium">{atualLabel}</span>
      <span className="mx-1.5 text-text-secondary">→</span>
      <span>{destino}</span>
      <span className="text-text-secondary">. Trial encerra ao aplicar.</span>
    </div>
  );
}
